import { useEffect, useRef, useState } from 'react';
import Brand from 'components/Brand';
import LinkItem from 'components/LinkItem';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  GridLegacy as Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Title from 'components/AuthTitle';
import Button from 'components/Button';
import BraincareLogo from 'assets/images/logo-braincare.png';
import { Logo } from 'components/Logo';
import Translator from 'components/Translator';
import { useTranslation } from 'react-i18next';
import { isCpf } from 'utils/validation';
import { formatToCpfUsername, unformatCpf } from 'utils/format';
import Footer from 'components/Footer';
import Fade from 'components/Fade';
import { discoverSso, isSsoEmail, redirectToSamlLogin } from 'api/sso';

function loginDocument(username) {
  const raw = String(username ?? '').trim();
  if (isCpf(raw)) {
    return unformatCpf(raw);
  }
  return raw;
}

function focusesPasswordOrSubmit(target) {
  if (!target || typeof target.closest !== 'function') {
    return false;
  }
  return Boolean(target.closest('[data-testid="password-input"], [data-testid="submit-button"]'));
}

function LoginForm({ isSubmitting, onSubmit }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState(new Map());
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [passwordLocked, setPasswordLocked] = useState(false);
  const [discovering, setDiscovering] = useState(false);

  const usernameRef = useRef('');
  const passwordRef = useRef('');
  const passwordInputRef = useRef(null);
  const runIdRef = useRef(0);
  const flightRef = useRef(null);
  const pendingPasswordFocusRef = useRef(false);

  usernameRef.current = username;
  passwordRef.current = password;

  useEffect(() => {
    if (passwordLocked || !pendingPasswordFocusRef.current) {
      return;
    }
    pendingPasswordFocusRef.current = false;
    passwordInputRef.current?.focus();
  }, [passwordLocked]);

  useEffect(() => {
    const releaseAfterBrowserBack = (event) => {
      if (!event.persisted) {
        return;
      }
      runIdRef.current += 1;
      flightRef.current = null;
      setDiscovering(false);
      setPasswordLocked(false);
    };
    window.addEventListener('pageshow', releaseAfterBrowserBack);
    return () => window.removeEventListener('pageshow', releaseAfterBrowserBack);
  }, []);

  const { t } = useTranslation();

  const USERNAME_EMPTY_ERROR = t('auth.login.doc-empty-error');
  const PASSWORD_EMPTY_ERROR = t('auth.login.pwd-empty-error');

  const requestDiscover = (email) => {
    const { current } = flightRef;
    if (current && current.email === email) {
      return current.promise;
    }
    const promise = discoverSso(email)
      .then((response) => {
        const idPublicOrg = response?.data?.id_public_org;
        if (!idPublicOrg) {
          return { kind: 'password' };
        }
        return { kind: 'sso', idPublicOrg };
      })
      .catch(() => ({ kind: 'password' }));
    flightRef.current = { email, promise };
    return promise;
  };

  const finishPasswordLogin = (documentValue) => {
    const currentPassword = passwordRef.current;
    if (!currentPassword) {
      pendingPasswordFocusRef.current = true;
      setErrors(new Map([['password', PASSWORD_EMPTY_ERROR]]));
      return;
    }
    pendingPasswordFocusRef.current = false;
    setErrors(new Map());
    onSubmit({ document: documentValue, password: currentPassword });
  };

  const runDiscover = async (email, { submit, focusPassword = false }) => {
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    setDiscovering(true);
    if (!passwordRef.current) {
      setPasswordLocked(true);
    }

    const result = await requestDiscover(email);
    if (runId !== runIdRef.current) {
      return;
    }
    if (loginDocument(usernameRef.current) !== email) {
      setDiscovering(false);
      setPasswordLocked(false);
      return;
    }
    if (result.kind === 'sso') {
      redirectToSamlLogin(result.idPublicOrg, email);
      return;
    }

    setDiscovering(false);
    setPasswordLocked(false);
    if (submit) {
      finishPasswordLogin(email);
      return;
    }
    if (focusPassword) {
      pendingPasswordFocusRef.current = true;
    }
  };

  const handleDocumentBlur = (event) => {
    if (!focusesPasswordOrSubmit(event.relatedTarget)) {
      return;
    }
    const documentValue = loginDocument(usernameRef.current);
    if (!isSsoEmail(documentValue)) {
      return;
    }
    runDiscover(documentValue, {
      submit: false,
      focusPassword: true,
    });
  };

  const handlePasswordFocus = () => {
    const documentValue = loginDocument(usernameRef.current);
    if (!isSsoEmail(documentValue) || flightRef.current?.email === documentValue) {
      return;
    }
    runDiscover(documentValue, { submit: false, focusPassword: true });
  };

  const handleSubmit = () => {
    const documentValue = loginDocument(username);
    const nextErrors = new Map();

    if (!documentValue) {
      nextErrors.set('username', USERNAME_EMPTY_ERROR);
    }

    if (documentValue && isSsoEmail(documentValue)) {
      setErrors(nextErrors);
      runDiscover(documentValue, { submit: true });
      return;
    }

    if (!password) {
      nextErrors.set('password', PASSWORD_EMPTY_ERROR);
    }

    if (nextErrors.size > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors(new Map());
    onSubmit({ document: documentValue, password });
  };

  const handleUsernameChange = (event) => {
    runIdRef.current += 1;
    flightRef.current = null;
    setUsername(event.target.value);
    setPasswordLocked(false);
    setDiscovering(false);
  };

  return (
    <>
      <Grid container sx={{ width: '100vw', height: '100vh' }} data-testid="login-form">
        <Grid item display={{ xs: 'none', md: 'block' }} md={7}>
          <Logo>
            <Brand image={BraincareLogo} title="Braincare logo" />
          </Logo>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              background: 'var(--color-white-secondary)',
              borderLeft: '2px solid var(--color-grey-light)',
              height: '100vh',
              padding: '40px',
            }}
            display="flex"
            alignItems="center"
          >
            <Fade>
              <Box sx={{ margin: '0 0 64px 0' }}>
                <Title data-testid="form-title">{t('auth.login.title-main-login')}</Title>
              </Box>

              <form onSubmit={(event) => event.preventDefault()}>
                <FormControl error={errors.has('username')} fullWidth>
                  <FormLabel data-testid="document-label">{t('auth.login.link-input-document')}</FormLabel>
                  <OutlinedInput
                    name="username"
                    value={isCpf(username) ? formatToCpfUsername(username) : username}
                    onChange={handleUsernameChange}
                    onBlur={handleDocumentBlur}
                    inputProps={{ 'data-testid': 'document-input' }}
                  />
                  <FormHelperText>{errors.get('username')}</FormHelperText>
                </FormControl>

                <FormControl error={errors.has('password')} fullWidth>
                  <FormLabel data-testid="password-label">{t('auth.login.input-pwd')}</FormLabel>
                  <OutlinedInput
                    name="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    onFocus={handlePasswordFocus}
                    type={isPasswordVisible ? 'text' : 'password'}
                    disabled={passwordLocked}
                    inputRef={passwordInputRef}
                    endAdornment={
                      passwordLocked ? (
                        <InputAdornment position="end">
                          <CircularProgress size={22} aria-label="Loading" />
                        </InputAdornment>
                      ) : (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                            edge="end"
                          >
                            {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                    inputProps={{ 'data-testid': 'password-input' }}
                  />
                  <FormHelperText>{errors.get('password')}</FormHelperText>
                </FormControl>

                <LinkItem href="/auth/forgot-password" data-testid="forgot-password-link">
                  {t('auth.login.link-password-reset')}
                </LinkItem>

                <Button
                  sx={{ margin: '32px 0 32px 0' }}
                  type="submit"
                  color="gradient"
                  size="large"
                  fullWidth
                  onClick={handleSubmit}
                  loading={isSubmitting || discovering}
                  data-testid="submit-button"
                >
                  {t('auth.login.button-login')}
                </Button>
              </form>
              <Footer />
            </Fade>
          </Box>
        </Grid>
      </Grid>
      <Translator />
    </>
  );
}

export default LoginForm;
